/**
 * What went wrong with a Shorts target on the way out, even if it is
 * `published` now (PAD-150).
 *
 * Status alone said "green" for every TikTok post after the 2026-10-21..28
 * outage and the 2026-10-29 crash, because each one did eventually post. The
 * backend now keeps the history (`failures`, `last_failure`, `missed_slot_at`
 * maintained by a trigger on shorts.targets); this turns it into words.
 */

/** A post this far past its slot is late. Normal runs land 1-31 min after. */
export const LATE_MINUTES = 60;
/** A queued target this far past its slot has missed it. */
export const OVERDUE_MINUTES = 30;

export interface TroubleFields {
  status: string;
  scheduled_at: string | null;
  native_schedule?: boolean;
  finished_at?: string | null;
  failures?: number | null;
  last_failure?: string | null;
  missed_slot_at?: string | null;
}

export const fmtLag = (minutes: number): string => {
  const m = Math.round(minutes);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const minutesBetween = (from: string, to: number) =>
  (to - new Date(from).getTime()) / 60_000;

/** Human-readable problems, empty when the target went out cleanly. */
export function troubles(t: TroubleFields, now: number = Date.now()): string[] {
  const out: string[] = [];
  // The FIRST slot it was given: a re-slot after an outage must not make a
  // week-late post read as on time against its new slot.
  const slot = t.missed_slot_at || t.scheduled_at;

  if ((t.status === 'pending' || t.status === 'uploading') && slot
      && !t.native_schedule) {
    const lag = minutesBetween(slot, now);
    if (lag > OVERDUE_MINUTES) out.push(`overdue by ${fmtLag(lag)}`);
  } else if (t.status === 'published' && slot && t.finished_at) {
    const lag = minutesBetween(slot, new Date(t.finished_at).getTime());
    if (lag > LATE_MINUTES) out.push(`posted ${fmtLag(lag)} late`);
  }
  if (t.missed_slot_at && t.status !== 'published') {
    out.push(`missed its ${new Date(t.missed_slot_at).toLocaleDateString(
      undefined, { month: 'short', day: 'numeric' })} slot`);
  }
  const n = t.failures ?? 0;
  if (n > 0) {
    out.push(`${n} failed attempt${n === 1 ? '' : 's'}`
      + (t.last_failure ? `: ${t.last_failure}` : ''));
  }
  return out;
}

/** Is it still waiting past its slot (as opposed to late but out)? */
export const isOverdue = (t: TroubleFields, now: number = Date.now()) =>
  (t.status === 'pending' || t.status === 'uploading')
  && troubles(t, now).some((s) => s.startsWith('overdue'));
